import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import ShalakaCell from './ShalakaCell';

const PATH_REVEAL_INTERVAL_MS = 300;
const POST_REVEAL_DELAY_MS = 100;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 4px;
  max-width: 640px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    max-width: 100%;
    gap: 3px;
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    gap: 2px;
  }
`;

function prefersReducedMotion() {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

const ShalakaGrid = ({ grid, gridTa, paths, onReadingRevealed }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [startCellIndex, setStartCellIndex] = useState(null);
  const [illuminatedCount, setIlluminatedCount] = useState(0);
  const timeoutsRef = useRef([]);

  const handleCellTap = useCallback((cellIndex) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setStartCellIndex(cellIndex);
    setIlluminatedCount(1);

    if (prefersReducedMotion()) {
      setIlluminatedCount(9);
      timeoutsRef.current.push(setTimeout(() => {
        onReadingRevealed(cellIndex);
      }, POST_REVEAL_DELAY_MS));
      return;
    }

    for (let step = 2; step <= 9; step++) {
      timeoutsRef.current.push(setTimeout(() => {
        setIlluminatedCount(step);
      }, (step - 1) * PATH_REVEAL_INTERVAL_MS));
    }
    timeoutsRef.current.push(setTimeout(() => {
      onReadingRevealed(cellIndex);
    }, 8 * PATH_REVEAL_INTERVAL_MS + POST_REVEAL_DELAY_MS));
  }, [isAnimating, onReadingRevealed]);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const computeCellState = (cellIndex) => {
    if (!isAnimating || startCellIndex === null) return 'idle';
    const path = paths[startCellIndex];
    if (cellIndex === startCellIndex && illuminatedCount >= 1) return 'tapped';
    const pathPosition = path.indexOf(cellIndex);
    if (pathPosition >= 1 && illuminatedCount > pathPosition) return 'path';
    if (pathPosition === -1) return 'dimmed';
    return 'idle';
  };

  return (
    <GridContainer role="grid" aria-label="Rama Shalaka 9 by 9 grid">
      {grid.flatMap((row, r) =>
        row.map((devanagari, c) => {
          const idx = r * 9 + c;
          return (
            <ShalakaCell
              key={idx}
              devanagari={devanagari}
              tamil={gridTa[r][c]}
              state={computeCellState(idx)}
              cellIndex={idx}
              onTap={() => handleCellTap(idx)}
              disabled={isAnimating}
            />
          );
        })
      )}
    </GridContainer>
  );
};

export default ShalakaGrid;
